import { z } from 'zod';

import { ProductKeySchema } from '../../billing/product-key/product-key';
import { UuidSchema } from '../../common/id/id';
import { DiscountGrantStatusSchema } from '../discount-grant-status/discount-grant-status';
import { DiscountSourceSchema } from '../discount-source/discount-source';
import { DiscountTypeSchema } from '../discount-type/discount-type';
import { StackingPolicySchema } from '../stacking-policy/stacking-policy';

/**
 * A discount a member has actually earned.
 *
 * ## Every commercial term is SNAPSHOT here, not read through to the rule
 *
 * `discountType`, `value`, `appliesToProductKeys` and `stackingPolicy` are
 * copies taken at issuance. Matrix row 18 requires it — editing a rule must not
 * change what a member was already promised — and the shape is what makes that
 * structural rather than a rule somebody has to remember when writing the
 * redemption query: there is no join through which the current rule could
 * influence an existing grant.
 *
 * `ruleId` is still carried, for `maxPerUser` counting and for the admin view's
 * "which rule issued this?" — but it is a *reference*, never a source of terms.
 */
export const DiscountGrantSchema = z
  .object({
    id: UuidSchema,
    userId: UuidSchema,
    ruleId: UuidSchema,
    /** Staff-facing label of the issuing rule, resolved for the admin list. */
    ruleName: z.string(),
    source: DiscountSourceSchema,
    /** The course or test that earned it — what the member's list links to. */
    sourceId: UuidSchema,
    discountType: DiscountTypeSchema,
    value: z.number(),
    appliesToProductKeys: z.array(ProductKeySchema),
    stackingPolicy: StackingPolicySchema,
    status: DiscountGrantStatusSchema,
    grantedAt: z.iso.datetime(),
    /** `null` when the issuing rule had no validity window (matrix row 14). */
    expiresAt: z.iso.datetime().nullable(),
    redeemedAt: z.iso.datetime().nullable(),
    revokedAt: z.iso.datetime().nullable(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
  })
  .strict();

export type DiscountGrant = z.infer<typeof DiscountGrantSchema>;

/**
 * The member's own view of a grant — the "discount status" surface (P22c).
 *
 * `ruleId` and `ruleName` are gone: operator shorthand is not a sentence to show
 * a member, and a rule id is a fact about the platform's configuration rather
 * than about their discount. What is left is exactly what answers "what do I
 * have, where does it apply, and until when?".
 *
 * ## `appliesToProductNames` is resolved server-side, and it has THREE readings
 *
 * `appliesToProductKeys` are operator strings (`premium.monthly`) — drawing one
 * to a member is the uuid-in-a-queue defect pointed the wrong way (P22b2). So
 * the names are resolved through `BILLING_GET_PRODUCT_NAMES` in the request's
 * locale (P22c3: a client interpolating a name it fetched separately is how an
 * Arabic reader meets an English plan), and the pair reads as:
 *
 * - **no keys** → every plan (matrix row 19), and the names are empty too;
 * - **keys with names** → exactly those plans, by name;
 * - **keys with NO names** → the plans it was scoped to are no longer offered.
 *   That query deliberately omits a key with no live product, because a grant
 *   outlives the plan it was scoped to — so this is a real state a surface has
 *   to draw, not a resolution failure.
 *
 * The names are **not** a second source of scope: redemption matches on the
 * keys, which are the snapshot. These are for reading.
 */
export const MemberDiscountGrantSchema = DiscountGrantSchema.omit({
  userId: true,
  ruleId: true,
  ruleName: true,
  createdAt: true,
  updatedAt: true,
})
  .extend({
    appliesToProductNames: z.array(z.string()),
  })
  .strict();

export type MemberDiscountGrant = z.infer<typeof MemberDiscountGrantSchema>;
